import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { api } from "@/api";
import type {
  AccountingIntegration,
  SyncQueueJob,
} from "@/api/accounting/integrations";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { ColumnType } from "@/app/components/tables/tableTypes";
import useErrorHandling from "@/hooks/useErrorHandling";
import { checkTypeLocale, statusColors, statusLocale } from "./ui";

interface ListIntegrationChecksModalProps {
  disclosure: ReturnType<typeof useDisclosure>;
  integration: AccountingIntegration | null;
}

export function ListIntegrationChecksModal({
  disclosure,
  integration,
}: ListIntegrationChecksModalProps) {
  const [checks, setChecks] = useState<SyncQueueJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const handleError = useErrorHandling();

  const loadChecks = useCallback(async () => {
    if (!integration) return;
    setIsLoading(true);
    try {
      const response = await api.accountingIntegrations.listChecks(
        integration.id,
      );
      setChecks(response.data);
    } catch (err) {
      const { errorMessage } = handleError(err as any);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [integration, handleError]);

  useEffect(() => {
    if (disclosure.isOpen && integration) {
      loadChecks();
    }
  }, [disclosure.isOpen, integration, loadChecks]);

  return (
    <Modal
      isOpen={disclosure.isOpen}
      onOpenChange={disclosure.onOpenChange}
      size="3xl">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              История синхронизаций: {integration?.displayName}
            </ModalHeader>
            <ModalBody>
              <TableBuilder
                displayOptions={false}
                removeWrapper={true}
                columns={[
                  {
                    key: "type",
                    label: "Тип",
                    type: ColumnType.Custom,
                    render: (value: string) => (
                      <>{checkTypeLocale[value] || value}</>
                    ),
                  },
                  {
                    key: "status",
                    label: "Статус",
                    type: ColumnType.Custom,
                    render: (value: string) => (
                      <Chip
                        size="sm"
                        variant="flat"
                        color={statusColors[value] || "default"}>
                        {statusLocale[value] || value}
                      </Chip>
                    ),
                  },
                  {
                    key: "createdAt",
                    label: "Дата",
                    type: ColumnType.Custom,
                    render: (value: string) => (
                      <div className="flex flex-col">
                        <span>
                          {new Date(value).toLocaleString("ru-RU", {
                            timeZone: "Etc/Utc",
                          })}
                        </span>
                      </div>
                    ),
                  },
                  {
                    key: "lastError",
                    label: "Ошибка",
                    type: ColumnType.String,
                  },
                ]}
                data={checks}
                rowsPerPage={5}
                defaultSortDescriptor={{
                  column: "createdAt",
                  direction: "descending",
                }}
              />
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="light" onPress={onClose}>
                Закрыть
              </Button>
              <Button
                color="primary"
                onPress={loadChecks}
                isLoading={isLoading}>
                Обновить
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
